import React, { useState, useEffect, useRef } from 'react';
import { db, storage, rtdb } from '../firebase';
import { collection, getDocs, doc, setDoc, getDoc, updateDoc, query, where, onSnapshot, orderBy, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ref as rtdbRef, onValue, off, get, set } from 'firebase/database';
import './iMessageThreads.css';
import { FaComment, FaCircle, FaChevronDown, FaChevronRight, FaEye, FaUser, FaBell, FaBellSlash, FaPlus, FaSearch } from 'react-icons/fa';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import dayjs from 'dayjs';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Layout from './Layout';
import notificationService from '../utils/notificationService';

// Use environment variable for API base, fallback to Fly.io proxy server if not set
const API_BASE = process.env.REACT_APP_API_BASE || 'https://vue-admin-proxy.fly.dev';

const REACTIONS = [
  { key: 'love', label: '❤️' },
  { key: 'like', label: '👍' },
  { key: 'dislike', label: '👎' },
  { key: 'laugh', label: '😂' },
  { key: 'exclaim', label: '❗' },
  { key: 'question', label: '❓' }
];

// Add this utility at the top (after imports):
function normalizePhoneNumber(phoneNumber) {
  if (!phoneNumber) return null;
  let cleaned = String(phoneNumber).replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    cleaned = cleaned.substring(1);
  }
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }
  if (cleaned.startsWith('1') && cleaned.length === 11) {
    return `+${cleaned}`;
  }
  if (cleaned.startsWith('+' )) {
    return cleaned;
  }
  if (phoneNumber.startsWith('+')) {
    return phoneNumber;
  }
  return `+${cleaned}`;
}

const IMessageThreads = () => {
  const [threads, setThreads] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newMsgId, setNewMsgId] = useState(null);
  const messagesEndRef = useRef(null);
  const [imageModal, setImageModal] = useState({ open: false, url: '' });
  const [activeReactionMsg, setActiveReactionMsg] = useState(null);
  const [showSentByUser, setShowSentByUser] = useState(null);
  const [reactionMenu, setReactionMenu] = useState({ open: false, messageId: null, anchor: null });
  const [reactionSent, setReactionSent] = useState({});
  const [reactionPickerPos, setReactionPickerPos] = useState(null);
  
  // Message recall states
  const [recallingMessage, setRecallingMessage] = useState(null);
  const [recallError, setRecallError] = useState(null);

  // User details modal state
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [userDetailsUser, setUserDetailsUser] = useState(null);
  const [userTakeovers, setUserTakeovers] = useState([]);
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [selectedTakeover, setSelectedTakeover] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [spotlightsOpen, setSpotlightsOpen] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editArrays, setEditArrays] = useState({ orders: [], campaign: [], spotlights: [] });
  const [newItem, setNewItem] = useState({ orders: '', campaign: '', spotlights: '' });
  const [signs, setSigns] = useState([]);

  // Unknown user management modal state
  const [showUnknownUserModal, setShowUnknownUserModal] = useState(false);
  const [unknownUserThread, setUnknownUserThread] = useState(null);
  const [matchingUsers, setMatchingUsers] = useState([]);
  const [loadingMatchingUsers, setLoadingMatchingUsers] = useState(false);
  const [matchSearchTerm, setMatchSearchTerm] = useState('');
  const [filteredMatchingUsers, setFilteredMatchingUsers] = useState([]);

  // 1. Add state for image upload and scroll tracking
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const convoBodyRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(true);

  // Add mobile view state
  const [mobileView, setMobileView] = useState('list'); // 'list' or 'chat'

  // Notification states
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [notificationSupported, setNotificationSupported] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  // Add refs for cleanup
  const pollIntervalRef = useRef(null);
  const lastFetchTimeRef = useRef(0);

  // Compose modal states
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [verifiedUsers, setVerifiedUsers] = useState([]);
  const [loadingVerifiedUsers, setLoadingVerifiedUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Add missing state for reply and match section UI
  const [replyUnknownMsg, setReplyUnknownMsg] = useState("");
  const [sendingUnknownReply, setSendingUnknownReply] = useState(false);
  const [matchSectionOpen, setShowMatchSectionOpen] = useState(false);

  // Helper to detect mobile - includes iPhone screen sizes
  const isMobile = typeof window !== 'undefined' && (
    window.innerWidth <= 768 || 
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
    window.innerWidth <= 414 // iPhone 12/13/14 Pro Max width
  );

  // Initialize notifications and set up push notification handlers
  useEffect(() => {
    const initializeNotifications = async () => {
      if (auth.currentUser) {
        setNotificationSupported(notificationService.isNotificationSupported());
        setNotificationPermission(notificationService.getPermissionStatus());
        
        // Check if notifications are enabled
        const status = await notificationService.getSubscriptionStatus();
        setNotificationEnabled(status.subscribed);
        
        // Initialize notification service if supported
        if (notificationService.isNotificationSupported()) {
          try {
            await notificationService.initialize(auth.currentUser.uid);
            setNotificationEnabled(true);
            
            // Set up push notification handler to update messages in real time
            notificationService.onPushNotification((notification) => {
              console.log('Push notification received:', notification);
              
              // Extract message data from notification
              if (notification.data && notification.data.userId && notification.data.messageId) {
                const { userId, messageId, message, direction, timestamp, attachments } = notification.data;
                
                // Update threads if the message is for a user in our threads list
                setThreads(prevThreads => {
                  const threadIndex = prevThreads.findIndex(thread => thread.user.id === userId);
                  if (threadIndex !== -1) {
                    const thread = prevThreads[threadIndex];
                    const newMessage = {
                      messageId,
                      message,
                      direction,
                      timestamp: new Date(timestamp),
                      attachments: attachments ? JSON.parse(attachments) : [],
                      userId
                    };
                    
                    // Check if message already exists
                    const messageExists = thread.messages.some(m => m.messageId === messageId);
                    let newMessages;
                    if (messageExists) {
                      newMessages = thread.messages.map(m => 
                        m.messageId === messageId ? { ...m, ...newMessage } : m
                      );
                    } else {
                      newMessages = [...thread.messages, newMessage];
                    }
                    
                    // Calculate new unseen count
                    const lastViewedKey = `lastViewed_${userId}`;
                    const lastViewed = localStorage.getItem(lastViewedKey);
                    const lastViewedTime = lastViewed ? new Date(lastViewed).getTime() : 0;
                    
                    const newUnseenCount = newMessages.filter(m => 
                      m.direction === 'inbound' && 
                      new Date(m.timestamp).getTime() > lastViewedTime
                    ).length;
                    
                    const updatedThreads = [...prevThreads];
                    updatedThreads[threadIndex] = {
                      ...thread,
                      messages: newMessages,
                      lastMsg: newMessage,
                      unseenCount: newUnseenCount
                    };
                    
                    // Sort threads by last message timestamp
                    updatedThreads.sort((a, b) => {
                      const aTime = a.lastMsg ? new Date(a.lastMsg.timestamp).getTime() : 0;
                      const bTime = b.lastMsg ? new Date(b.lastMsg.timestamp).getTime() : 0;
                      return bTime - aTime;
                    });
                    
                    return updatedThreads;
                  }
                  return prevThreads;
                });
                
                // Update current messages if this is the selected user
                if (selectedUser && selectedUser.id === userId) {
                  // setMessages(prevMessages => { // This line is removed
                  //   const messageExists = prevMessages.some(m => m.messageId === messageId);
                  //   if (messageExists) {
                  //     return prevMessages.map(m => 
                  //       m.messageId === messageId ? { ...m, message, direction, timestamp: new Date(timestamp), attachments: attachments ? JSON.parse(attachments) : [] } : m
                  //     );
                  //   } else {
                  //     const newMessage = {
                  //       messageId,
                  //       message,
                  //       direction,
                  //       timestamp: new Date(timestamp),
                  //       attachments: attachments ? JSON.parse(attachments) : [],
                  //       userId
                  //     };
                  //     setNewMsgId(messageId);
                  //     return [...prevMessages, newMessage];
                  //   }
                  // });
                }
              } else {
                // If we don't have specific message data, refresh threads to get latest messages
                console.log('No specific message data in notification, refreshing threads...');
                fetchThreads();
                
                // Also refresh current user messages if we have a selected user
                if (selectedUser) {
                  refreshCurrentUserMessages();
                }
              }
            });
            
          } catch (error) {
            console.log('Notification initialization failed:', error);
            setNotificationEnabled(false);
          }
        }
      }
    };
    
    initializeNotifications();
    
    // Cleanup notification handler on unmount
    return () => {
      notificationService.offPushNotification();
    };
  }, [auth.currentUser, selectedUser]);

  // Optimized message fetching with caching
  const refreshCurrentUserMessages = async () => {
    if (!selectedUser) return;
    
    try {
      // Add loading state for better UX
      setLoading(true);
      
      const res = await fetch(`${API_BASE}/api/messages/${selectedUser.id}`);
      if (res.ok) {
        const data = await res.json();
        const newMessages = data.messages || [];
        
        if (newMessages.length > 0) {
          // Merge with existing messages more efficiently
          const serverMessages = newMessages.map(msg => normalizeMessage(msg));
          
          // Use Set for faster deduplication
          const messageIds = new Set();
          const mergedMessages = [];
          
          // Add existing messages first
          messages.forEach(msg => {
            if (msg.messageId && !messageIds.has(msg.messageId)) {
              messageIds.add(msg.messageId);
              mergedMessages.push(msg);
            }
          });
          
          // Add/update with server messages
          serverMessages.forEach(msg => {
            if (msg.messageId) {
              const existingIndex = mergedMessages.findIndex(m => m.messageId === msg.messageId);
              if (existingIndex !== -1) {
                // Update existing message
                mergedMessages[existingIndex] = { ...mergedMessages[existingIndex], ...msg };
              } else {
                // Add new message
                messageIds.add(msg.messageId);
                mergedMessages.push(msg);
              }
            }
          });
          
          // Sort by timestamp
          const sortedMessages = mergedMessages.sort((a, b) => 
            getSafeTimestamp(a.timestamp) - getSafeTimestamp(b.timestamp)
          );
          
          // Load reactions BEFORE setting messages to avoid double render
          try {
            const messagesWithReactions = await loadReactionsForUser(selectedUser.id, sortedMessages);
            setMessages(messagesWithReactions);
          } catch (error) {
            console.error('Error loading reactions:', error);
            // Still set messages even if reactions fail to load
            setMessages(sortedMessages);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing current user messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // When selecting a user, show their messages
  const handleSelectUser = async (thread) => {
    setSelectedUser(thread.user);
    setLoading(true);
    
    try {
      // Start with cached messages for immediate display
      console.log(`Setting cached messages for user ${thread.user.id}:`, thread.messages.length);
      
      // Load reactions for cached messages before setting them
      try {
        const messagesWithReactions = await loadReactionsForUser(thread.user.id, thread.messages);
        setMessages(messagesWithReactions);
      } catch (error) {
        console.error('Error loading reactions for cached messages:', error);
        setMessages(thread.messages);
      }
      
      // Load full message history in background if needed
      if (thread.messages.length < 50) { // If we have less than 50 messages, load full history
        console.log('Loading full message history...');
        const fullMessages = await loadFullMessageHistory(thread.user.id);
        if (fullMessages.length > thread.messages.length) {
          console.log(`Loaded ${fullMessages.length} full messages vs ${thread.messages.length} cached`);
          
          // Load reactions for full messages before setting them
          try {
            const fullMessagesWithReactions = await loadReactionsForUser(thread.user.id, fullMessages);
            setMessages(fullMessagesWithReactions);
          } catch (error) {
            console.error('Error loading reactions for full messages:', error);
            setMessages(fullMessages);
          }
          
          // Update thread with full messages
          setThreads(prev => prev.map(t => t.user.id === thread.user.id ? {
            ...t,
            messages: fullMessages,
            lastMsg: fullMessages[fullMessages.length - 1]
          } : t));
        }
      }
    } catch (error) {
      console.error('Error loading user messages:', error);
    } finally {
      setLoading(false);
    }
    
    setIsCreatingThread(false);
    
    // Store current timestamp as last viewed time for this user
    const currentTime = new Date().toISOString();
    const lastViewedKey = `lastViewed_${thread.user.id}`;
    localStorage.setItem(lastViewedKey, currentTime);
    
    // Mark unseen inbound messages as seen (API and Firestore)
    const unseenInboundMessages = thread.messages.filter(m => 
      m.direction === 'inbound' && !m.seen
    );
    
    if (unseenInboundMessages.length > 0) {
      const messageIds = unseenInboundMessages.map(m => m.messageId);
      try {
        // Mark as seen via API
        await fetch(`${API_BASE}/api/messages/mark-seen`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messageIds })
        });
      } catch (error) {
        console.error('Error marking messages as seen via API:', error);
      }
      try {
        // Mark as seen in Firestore
        await Promise.all(
          messageIds.map(id =>
            updateDoc(doc(db, 'imessages', id), { seen: true })
          )
        );
      } catch (error) {
        console.error('Error marking messages as seen in Firestore:', error);
      }
      // Update local messages to mark as seen
      setMessages(prev => prev.map(m => 
        m.direction === 'inbound' ? { ...m, seen: true } : m
      ));
    }
    
    // Update threads to reflect that this user's messages are now seen
    setThreads(prev => prev.map(t => t.user.id === thread.user.id ? {
      ...t,
      unseenCount: 0,
      lastViewedTime: new Date(currentTime).getTime()
    } : t));
  };

  // When selecting a verified user without messages, start a new conversation
  // const handleSelectVerifiedUser = (user) => {
  //   setSelectedUser(user);
  //   setMessages([]);
  //   setIsCreatingThread(true);
  // };

  // When a thread is selected on mobile, switch to chat view
  const handleSelectUserMobile = (thread) => {
    setSelectedUser(thread.user);
    setMobileView('chat');
    setIsCreatingThread(false);
  };

  // When back is pressed on mobile, go back to list
  const handleBackToList = () => {
    setMobileView('list');
    setSelectedUser(null);
  };

  // 2. Add image upload handler
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `imessage_attachments/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      // Send as attachment
      await handleSendMessage('', [url]);
    } catch (err) {
      alert('Failed to upload image');
    }
    setUploadingImage(false);
  };

  // 3. Update handleSendMessage to accept attachments
  const handleSendMessage = async (msgText, attachments = []) => {
    let text = typeof msgText === 'string' ? msgText : newMessage;
    if (!text.trim() && attachments.length > 0) {
      text = ' ';
    }
    if (!text.trim() && attachments.length === 0) return;
    if (!selectedUser || sendingMessage) return;
    setSendingMessage(true);
    try {
      const formattedPhone = normalizePhoneNumber(selectedUser.phoneNumber);
      if (!formattedPhone) {
        alert('Invalid phone number format');
        setSendingMessage(false);
        return;
      }
      // No optimistic UI update here
      const res = await fetch(`${API_BASE}/api/send-imessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: formattedPhone,
          message: text.trim(),
          userId: selectedUser.id,
          attachments: attachments.length > 0 ? attachments : undefined
        })
      });
      if (res.ok) {
        setNewMessage('');
        setImageFile(null);
        // No need to manually refresh, Firestore onSnapshot will update
      }
    } catch (e) {}
    setSendingMessage(false);
  };

  // Tapback (reaction) handler
  const handleBubbleClick = (msg, e) => {
    if (msg.messageId === activeReactionMsg) {
      setActiveReactionMsg(null);
      setReactionPickerPos(null);
    } else {
      setActiveReactionMsg(msg.messageId);
      if (e) {
        const rect = e.currentTarget.getBoundingClientRect();
        setReactionPickerPos({ x: rect.left + rect.width / 2, y: rect.top, msg });
      }
    }
  };

  const handleSelectReaction = async (reactionKey, msg) => {
    setReactionMenu({ open: false, messageId: null, anchor: null });
    setActiveReactionMsg(null);
    setReactionPickerPos(null);
    
    // Update UI immediately for better UX
    setMessages(prev => prev.map(m => m.messageId === msg.messageId ? { ...m, reaction: reactionKey, reactionSentAt: new Date().toISOString() } : m));
    setReactionSent(prev => ({ ...prev, [msg.messageId]: true }));
    
    try {
      // Save reaction to Firebase first
      const reactionData = {
        messageId: msg.messageId,
        userId: selectedUser.id,
        reaction: reactionKey,
        timestamp: new Date().toISOString(),
        adminUser: auth.currentUser?.email || auth.currentUser?.uid || 'unknown'
      };
      
      // Save to reactions collection
      await setDoc(doc(db, 'reactions', `${msg.messageId}_${selectedUser.id}`), reactionData);
      
      // Also save to user's reactions subcollection for easier querying
      await setDoc(doc(db, 'users', selectedUser.id, 'reactions', msg.messageId), reactionData);
      
      // Send reaction via API
      const res = await fetch(`${API_BASE}/api/send-reaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient: msg.phoneNumber,
          message_id: msg.messageId,
          reaction: reactionKey,
          sender_name: 'vue-billboards@a.imsg.co',
          userId: selectedUser.id
        })
      });
      
      // Check if response is ok first
      if (!res.ok) {
        console.error('Reaction API error:', res.status, res.statusText);
        // Reaction is already saved to Firebase, so we're good
        setTimeout(() => setReactionSent(prev => ({ ...prev, [msg.messageId]: false })), 2000);
        return;
      }
      
      // Try to parse JSON response
      let result;
      try {
        result = await res.json();
      } catch (jsonError) {
        console.log('Non-JSON response from reaction API, but reaction was sent and saved to Firebase');
        setTimeout(() => setReactionSent(prev => ({ ...prev, [msg.messageId]: false })), 2000);
        return;
      }
      
      if (result.success) {
        console.log('Reaction sent successfully and saved to Firebase');
      } else {
        console.error('Reaction API returned success: false, but saved to Firebase');
      }
      
      setTimeout(() => setReactionSent(prev => ({ ...prev, [msg.messageId]: false })), 2000);
    } catch (error) {
      console.error('Error sending reaction:', error);
      // Even if there's an error, the reaction is saved to Firebase
      setTimeout(() => setReactionSent(prev => ({ ...prev, [msg.messageId]: false })), 2000);
    }
  };

  // Message recall handler
  const handleRecallMessage = async (msg) => {
    if (!selectedUser || !msg || msg.direction !== 'outbound') {
      return;
    }

    // Check if message is too old to recall (2 minutes)
    const messageTime = new Date(msg.timestamp);
    const now = new Date();
    const timeDiff = now.getTime() - messageTime.getTime();
    const maxRecallTime = 2 * 60 * 1000; // 2 minutes

    if (timeDiff > maxRecallTime) {
      setRecallError('Message is too old to recall (must be within 2 minutes)');
      setTimeout(() => setRecallError(null), 3000);
      return;
    }

    // Check if message is already recalled
    if (msg.recalled) {
      setRecallError('Message has already been recalled');
      setTimeout(() => setRecallError(null), 3000);
      return;
    }

    setRecallingMessage(msg.messageId);
    setRecallError(null);

    try {
      const response = await fetch(`${API_BASE}/api/recall-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messageId: msg.messageId,
          userId: selectedUser.id,
          recipient: msg.phoneNumber,
          adminId: auth.currentUser?.email || auth.currentUser?.uid || 'unknown'
        })
      });

      const result = await response.json();

      if (result.success) {
        // Update the message in the UI to show it's recalled
        setMessages(prev => prev.map(m => 
          m.messageId === msg.messageId 
            ? { ...m, recalled: true, recalledAt: result.recalledAt }
            : m
        ));
        
        console.log('Message recalled successfully');
      } else {
        setRecallError(result.error || 'Failed to recall message');
        setTimeout(() => setRecallError(null), 3000);
      }
    } catch (error) {
      console.error('Error recalling message:', error);
      setRecallError('Error recalling message');
      setTimeout(() => setRecallError(null), 3000);
    } finally {
      setRecallingMessage(null);
    }
  };

  // Add a helper to get sender name
  const getSenderName = (msg) => {
    if (msg.direction === 'outbound') {
      if (auth && auth.currentUser) {
        return auth.currentUser.displayName || auth.currentUser.email || auth.currentUser.uid || 'You';
      }
      return 'You';
    }
    return msg.senderName || 'User';
  };

  // Helper to safely format time from Firestore Timestamp, string, or Date
  function getFormattedTime(msg) {
    let formattedTime = '';
    let dateObj = null;
    if (msg.createdAt) {
      if (typeof msg.createdAt === 'object' && msg.createdAt.seconds) {
        dateObj = new Date(msg.createdAt.seconds * 1000);
      } else {
        dateObj = new Date(msg.createdAt);
      }
    } else if (msg.timestamp) {
      if (typeof msg.timestamp === 'object' && msg.timestamp.seconds) {
        dateObj = new Date(msg.timestamp.seconds * 1000);
      } else {
        dateObj = new Date(msg.timestamp);
      }
    }
    if (dateObj && !isNaN(dateObj.getTime())) {
      formattedTime = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return formattedTime;
  }

  // 4. Scroll tracking logic
  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, autoScroll]);



  // Notification handlers
  const handleEnableNotifications = async () => {
    try {
      if (!auth.currentUser) {
        alert('Please log in to enable notifications');
        return;
      }
      console.log('[iMessageThreads] handleEnableNotifications: calling notificationService.initialize for user', auth.currentUser.uid);
      await notificationService.initialize(auth.currentUser.uid);
      setNotificationEnabled(true);
      setNotificationPermission(notificationService.getPermissionStatus());
      setShowNotificationPrompt(false);
    } catch (error) {
      console.error('[iMessageThreads] Error enabling notifications:', error);
      alert('Failed to enable notifications. Please check your browser settings.');
    }
  };

  const handleDisableNotifications = async () => {
    try {
      await notificationService.unsubscribeFromPushNotifications();
      setNotificationEnabled(false);
    } catch (error) {
      console.error('Error disabling notifications:', error);
    }
  };

  const handleTestNotification = async () => {
    try {
      await notificationService.testNotification();
    } catch (error) {
      console.error('Error testing notification:', error);
      alert('Failed to show test notification. Please check your browser settings.');
    }
  };

  // Enhanced unread count calculation
  const calculateUnreadCount = (messages, userId) => {
    const lastViewedKey = `lastViewed_${userId}`;
    const lastViewed = localStorage.getItem(lastViewedKey);
    const lastViewedTime = lastViewed ? new Date(lastViewed).getTime() : 0;
    
    return messages.filter(m => {
      if (m.direction !== 'inbound') return false;
      
      const messageTime = getSafeTimestamp(m.timestamp);
      return messageTime > lastViewedTime;
    }).length;
  };

  // Clear cache when needed (e.g., after new messages)
  const clearThreadsCache = () => {
    localStorage.removeItem('threads_cache');
  };

  // Load full message history for a specific user (lazy loading) from Realtime Database
  const loadFullMessageHistory = async (userId) => {
    try {
      const userMessagesRef = rtdbRef(rtdb, `imessages/${userId}`);
      const snapshot = await get(userMessagesRef);
      const data = snapshot.val();
      
      if (!data) {
        return [];
      }
      
      const fullMessages = Object.keys(data).map(messageId => {
        const messageData = data[messageId];
        return {
          ...messageData,
          messageId: messageId,
          timestamp: messageData.timestamp ? new Date(messageData.timestamp) : new Date()
        };
      });
      
      // Sort by timestamp
      fullMessages.sort((a, b) => getSafeTimestamp(a.timestamp) - getSafeTimestamp(b.timestamp));
      
      return fullMessages;
    } catch (error) {
      console.error(`Error loading full message history for user ${userId}:`, error);
      return [];
    }
  };

  // Optimized thread fetching with better unread calculation and reduced queries
  const fetchThreads = async () => {
    try {
      // Prevent multiple simultaneous fetches
      const now = Date.now();
      if (now - lastFetchTimeRef.current < 5000) { // 5 second cooldown
        console.log('Skipping fetch - too soon since last fetch');
        return;
      }
      lastFetchTimeRef.current = now;
      
      setLoading(true);
      
      // Use cached data if available and recent (within 30 seconds)
      const cacheKey = 'threads_cache';
      const cacheData = localStorage.getItem(cacheKey);
      if (cacheData) {
        try {
          const { data, timestamp } = JSON.parse(cacheData);
          const cacheAge = Date.now() - timestamp;
          if (cacheAge < 30000) { // 30 seconds cache
            console.log('Using cached threads data');
            // Deduplicate messages in each thread
            const dedupedThreads = data.threads.map(thread => ({
              ...thread,
              messages: dedupeMessages(thread.messages)
            }));
            setThreads(dedupedThreads);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.log('Cache invalid, fetching fresh data');
        }
      }

      console.log('Fetching fresh threads data...');
      
      // Get all messages from Realtime Database and filter by date
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const imessagesRef = rtdbRef(rtdb, 'imessages');
      const messagesSnapshot = await get(imessagesRef);
      const imessagesData = messagesSnapshot.val();
      
      let allMessages = [];
      if (imessagesData) {
        // Convert nested object structure to flat array
        Object.keys(imessagesData).forEach(userId => {
          const userMessages = imessagesData[userId];
          Object.keys(userMessages).forEach(messageId => {
            const messageData = userMessages[messageId];
            const messageTimestamp = messageData.timestamp ? new Date(messageData.timestamp) : new Date();
            
            // Filter by date (last 7 days)
            if (messageTimestamp >= sevenDaysAgo) {
              allMessages.push({
                ...messageData,
                messageId: messageId,
                userId: userId,
                timestamp: messageTimestamp
              });
            }
          });
        });
      }
      
      // Get unique user IDs from messages
      const userIdsWithMessages = [...new Set(allMessages.map(msg => msg.userId))];
      console.log(`Found ${userIdsWithMessages.length} users with messages`);
      
      // Batch fetch all users in one operation
      const userPromises = userIdsWithMessages.map(async (userId) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            return { id: userDoc.id, ...userDoc.data() };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
          return null;
        }
      });
      
      const users = (await Promise.all(userPromises)).filter(Boolean);
      console.log(`Successfully fetched ${users.length} users`);
      
      // Group messages by userId
      const messagesByUser = new Map();
      allMessages.forEach(msg => {
        if (!messagesByUser.has(msg.userId)) {
          messagesByUser.set(msg.userId, []);
        }
        messagesByUser.get(msg.userId).push(msg);
      });
      
      // Build threads efficiently
      const threads = [];
      
      for (const [userId, userMessages] of messagesByUser) {
        const user = users.find(u => u.id === userId);
        if (!user) continue;
        
        // Sort messages by timestamp
        userMessages.sort((a, b) => getSafeTimestamp(a.timestamp) - getSafeTimestamp(b.timestamp));
        
        const lastMsg = userMessages[userMessages.length - 1];
        const unseenCount = calculateUnreadCount(userMessages, userId);
        
        threads.push({
          user,
          messages: userMessages,
          lastMsg,
          unseenCount,
          lastViewedTime: localStorage.getItem(`lastViewed_${userId}`) ? 
            new Date(localStorage.getItem(`lastViewed_${userId}`)).getTime() : 0
        });
      }
      
      // Also fetch unmatched messages from the unmatched_messages collection
      try {
        const unmatchedMessagesRef = collection(db, 'unmatched_messages');
        const unmatchedQuery = query(
          unmatchedMessagesRef,
          where('timestamp', '>=', sevenDaysAgo)
        );
        
        const unmatchedSnapshot = await getDocs(unmatchedQuery);
        const unmatchedMessages = unmatchedSnapshot.docs.map(doc => ({
          ...doc.data(),
          messageId: doc.id,
          timestamp: doc.data().timestamp ? new Date(doc.data().timestamp) : new Date()
        }));
        
        // Group unmatched messages by phone number
        const unmatchedByPhone = new Map();
        unmatchedMessages.forEach(msg => {
          const phoneKey = normalizePhoneNumber(msg.phoneNumber || msg.searchedPhoneNumber || 'unknown');
          if (!unmatchedByPhone.has(phoneKey)) {
            unmatchedByPhone.set(phoneKey, []);
          }
          unmatchedByPhone.get(phoneKey).push(msg);
        });
        
        // Create threads for unmatched messages
        for (const [phoneNumber, messages] of unmatchedByPhone) {
          messages.sort((a, b) => getSafeTimestamp(a.timestamp) - getSafeTimestamp(b.timestamp));
          const lastMsg = messages[messages.length - 1];
          
          threads.push({
            user: {
              id: `unknown_${phoneNumber}`,
              name: `Unknown User (${phoneNumber})`,
              email: '',
              phoneNumber: phoneNumber,
              phoneVerified: false,
              isUnknown: true
            },
            messages: messages,
            lastMsg: lastMsg,
            unseenCount: calculateUnreadCount(messages, `unknown_${phoneNumber}`),
            lastViewedTime: localStorage.getItem(`lastViewed_unknown_${phoneNumber}`) ? 
              new Date(localStorage.getItem(`lastViewed_unknown_${phoneNumber}`)).getTime() : 0,
            isUnknown: true
          });
        }
        
        console.log(`Found ${unmatchedMessages.length} unmatched messages from ${unmatchedByPhone.size} unknown users`);
      } catch (error) {
        console.error('Error fetching unmatched messages:', error);
      }
      
      // Sort threads by last message timestamp
      threads.sort((a, b) => {
        const aTime = a.lastMsg ? getSafeTimestamp(a.lastMsg.timestamp) : 0;
        const bTime = b.lastMsg ? getSafeTimestamp(b.lastMsg.timestamp) : 0;
        return bTime - aTime;
      });
      
      setThreads(threads);
      
      // Cache the results
      const cacheResult = {
        threads,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheResult));
      
      console.log(`Loaded ${threads.length} threads (including unknown users)`);
      
    } catch (error) {
      console.error('Error fetching threads:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch — wait for auth to resolve before reading RTDB
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchThreads();
      }
    });
    return unsubscribe;
  }, []);

  // Dismiss reaction picker when clicking outside
  useEffect(() => {
    if (!activeReactionMsg) return;
    const handler = () => { setActiveReactionMsg(null); setReactionPickerPos(null); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [activeReactionMsg]);



     // Load reactions from Firebase for a user's messages
   const loadReactionsForUser = async (userId, messages) => {
     try {
       // Only load reactions if we have messages to check
       if (!messages || messages.length === 0) {
         return messages;
       }
       
       // Get message IDs that we need reactions for
       const messageIds = messages.map(msg => msg.messageId).filter(Boolean);
       
       if (messageIds.length === 0) {
         return messages;
       }
       
       // Batch load reactions for all message IDs
       const reactionsSnap = await getDocs(collection(db, 'users', userId, 'reactions'));
       const reactions = {};
       
       reactionsSnap.docs.forEach(doc => {
         const data = doc.data();
         if (messageIds.includes(data.messageId)) {
           reactions[data.messageId] = data.reaction;
         }
       });
       
       // Update messages with reactions
       return messages.map(msg => ({
         ...msg,
         reaction: reactions[msg.messageId] || null
       }));
     } catch (error) {
       console.error('Error loading reactions:', error);
       return messages;
     }
   };

   // Scroll to bottom when messages change
   useEffect(() => {
     if (messagesEndRef.current && autoScroll) {
       messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
     }
   }, [messages, autoScroll]);

   // Remove animation class after animation
   useEffect(() => {
     if (newMsgId) {
       const timeout = setTimeout(() => setNewMsgId(null), 600);
       return () => clearTimeout(timeout);
     }
   }, [newMsgId]);

     // Enhanced scroll handler for better mobile experience
  const handleScroll = () => {
    if (convoBodyRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = convoBodyRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setIsAtBottom(isAtBottom);
      setAutoScroll(isAtBottom);
    }
  };

  // Mobile-specific optimizations
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      if (newIsMobile !== isMobile) {
        // Force re-render on mobile/desktop switch
        window.location.reload();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Optimized polling with proper cleanup and reduced frequency
  useEffect(() => {
    if (!threads.length) return;
    
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    const pollInterval = setInterval(async () => {
      // Only poll if we have a selected user and it's been at least 10 seconds since last fetch
      if (selectedUser && (Date.now() - lastFetchTimeRef.current > 10000)) {
        try {
          const res = await fetch(`${API_BASE}/api/messages/${selectedUser.id}`);
          if (res.ok) {
            const data = await res.json();
            const newMessages = data.messages || [];
            
            // Only update if we have new messages to avoid unnecessary re-renders
            if (newMessages.length > 0) {
              const serverMessages = newMessages.map(msg => normalizeMessage(msg));
              
              // Use Set for faster deduplication
              const messageIds = new Set();
              const mergedMessages = [];
              
              // Add existing messages first
              messages.forEach(msg => {
                if (msg.messageId && !messageIds.has(msg.messageId)) {
                  messageIds.add(msg.messageId);
                  mergedMessages.push(msg);
                }
              });
              
              // Add/update with server messages
              serverMessages.forEach(msg => {
                if (msg.messageId) {
                  const existingIndex = mergedMessages.findIndex(m => m.messageId === msg.messageId);
                  if (existingIndex !== -1) {
                    // Update existing message
                    mergedMessages[existingIndex] = { ...mergedMessages[existingIndex], ...msg };
                  } else {
                    // Add new message
                    messageIds.add(msg.messageId);
                    mergedMessages.push(msg);
                  }
                }
              });
              
              // Sort by timestamp
              const sortedMessages = mergedMessages.sort((a, b) => 
                getSafeTimestamp(a.timestamp) - getSafeTimestamp(b.timestamp)
              );
              
              // Load reactions before setting messages to avoid double render
              try {
                const messagesWithReactions = await loadReactionsForUser(selectedUser.id, sortedMessages);
                setMessages(messagesWithReactions);
              } catch (error) {
                console.error('Error loading reactions during poll:', error);
                setMessages(sortedMessages);
              }
            }
          }
        } catch (e) {
          console.error('Error polling messages:', e);
        }
      }
      
      // Update unread counts for all threads (less frequently)
      if (Date.now() % 30000 < 5000) { // Only every 30 seconds
        setThreads(prevThreads => {
          return prevThreads.map(thread => {
            const lastViewedKey = `lastViewed_${thread.user.id}`;
            const lastViewed = localStorage.getItem(lastViewedKey);
            const lastViewedTime = lastViewed ? new Date(lastViewed).getTime() : 0;
            
            const newUnseenCount = thread.messages.filter(m => 
              m.direction === 'inbound' && 
              getSafeTimestamp(m.timestamp) > lastViewedTime
            ).length;
            
            return {
              ...thread,
              unseenCount: newUnseenCount
            };
          });
        });
      }
    }, isMobile ? 15000 : 10000); // Reduced polling frequency: 15s on mobile, 10s on desktop
    
    // Store the interval reference for cleanup
    pollIntervalRef.current = pollInterval;
    
    // Cleanup function
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [threads, selectedUser, isMobile]);

   // Auto-scroll to bottom when new message arrives (if user is at bottom)
   useEffect(() => {
     if (autoScroll && messagesEndRef.current) {
       messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
     }
   }, [messages.length, autoScroll]);

  // Real-time Realtime Database listener for selected user's messages
  useEffect(() => {
    if (!selectedUser) return;
    
    // Listen to messages for the selected user in Realtime Database
    const userMessagesRef = rtdbRef(rtdb, `imessages/${selectedUser.id}`);
    
    const unsubscribe = onValue(userMessagesRef, async (snapshot) => {
      const data = snapshot.val();
      let newMessages = [];
      
      if (data) {
        // Convert object to array of messages
        newMessages = Object.keys(data).map(messageId => {
          const messageData = data[messageId];
          return normalizeMessage({
            ...messageData,
            messageId: messageId
          });
        });
      }
      
      // Deduplicate and sort
      newMessages = dedupeMessages(newMessages).sort((a, b) => getSafeTimestamp(a.timestamp) - getSafeTimestamp(b.timestamp));
      
      // Debug: Log message order for verification
      if (newMessages.length > 0) {
        console.log('Message order after sorting:', newMessages.map(msg => ({
          message: msg.message,
          timestamp: msg.timestamp,
          direction: msg.direction,
          messageId: msg.messageId
        })));
      }
      
      // Load reactions before setting messages
      try {
        const messagesWithReactions = await loadReactionsForUser(selectedUser.id, newMessages);
        setMessages(messagesWithReactions);
      } catch (error) {
        setMessages(newMessages);
      }
    });
    
    return () => {
      off(userMessagesRef, 'value', unsubscribe);
    };
  }, [selectedUser]);

  // Cleanup effect - ensure all listeners and intervals are cleaned up on unmount
  useEffect(() => {
    return () => {
      // Clean up polling interval
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      
      // Clear any cached data to prevent stale data on next mount
      localStorage.removeItem('threads_cache');
      
      console.log('iMessageThreads component cleaned up');
    };
  }, []);

  // Utility to normalize a message object
  function normalizeMessage(msg) {
    // Ensure messageId is present
    const messageId = msg.messageId || msg.id || '';
    // Normalize timestamp to JS Date
    let timestamp = msg.timestamp;
    if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
      timestamp = new Date(timestamp.seconds * 1000);
    } else if (typeof timestamp === 'string') {
      timestamp = new Date(timestamp);
    } else if (!(timestamp instanceof Date)) {
      timestamp = new Date();
    }
    
    // Validate the timestamp - if it's invalid, use current time
    if (timestamp instanceof Date && isNaN(timestamp.getTime())) {
      console.warn('Invalid timestamp found in message:', msg);
      timestamp = new Date();
    }
    
    return {
      ...msg,
      messageId,
      timestamp,
    };
  }

  // Helper function to safely get timestamp value
  function getSafeTimestamp(timestamp) {
    if (timestamp instanceof Date && !isNaN(timestamp.getTime())) {
      return timestamp.getTime();
    } else if (timestamp) {
      const parsedTime = new Date(timestamp);
      return isNaN(parsedTime.getTime()) ? 0 : parsedTime.getTime();
    }
    return 0;
  }

  // Utility to deduplicate messages by messageId
  function dedupeMessages(messages) {
    const map = new Map();
    messages.forEach(msg => {
      const normMsg = normalizeMessage(msg);
      
      // Use messageId as the primary key for deduplication
      // This ensures we don't lose messages that arrive within the same minute
      const key = normMsg.messageId || `${normMsg.direction}|${normMsg.message}|${normMsg.userId}|${normMsg.timestamp}`;
      
      if (!map.has(key)) {
        map.set(key, normMsg);
      } else {
        // If we have a duplicate messageId, keep the one with the most recent timestamp
        const existing = map.get(key);
        const existingTime = getSafeTimestamp(existing.timestamp);
        const newTime = getSafeTimestamp(normMsg.timestamp);
        
        if (newTime > existingTime) {
          map.set(key, normMsg);
        }
      }
    });
    return Array.from(map.values());
  }

  // Fetch verified users for compose modal
  const fetchVerifiedUsers = async () => {
    try {
      setLoadingVerifiedUsers(true);
      
      // Get all users from Firebase
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const users = [];
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        // Only include users with verified phone numbers
        if (userData.phoneVerified === true && userData.phoneNumber && userData.phoneNumber.trim() !== '') {
          users.push({
            id: doc.id,
            name: userData.name || userData.email || 'Unknown User',
            email: userData.email || '',
            phoneNumber: userData.phoneNumber,
            phoneVerified: userData.phoneVerified
          });
        }
      });
      
      // Sort by name
      users.sort((a, b) => a.name.localeCompare(b.name));
      
      setVerifiedUsers(users);
      setFilteredUsers(users);
      console.log(`Found ${users.length} verified users`);
    } catch (error) {
      console.error('Error fetching verified users:', error);
      setVerifiedUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoadingVerifiedUsers(false);
    }
  };

  // Filter users based on search term
  const filterUsers = (term) => {
    if (!term.trim()) {
      setFilteredUsers(verifiedUsers);
      return;
    }
    
    const filtered = verifiedUsers.filter(user => 
      user.name.toLowerCase().includes(term.toLowerCase()) ||
      user.email.toLowerCase().includes(term.toLowerCase()) ||
      user.phoneNumber.includes(term)
    );
    
    setFilteredUsers(filtered);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    filterUsers(term);
  };

  // Start new thread with selected user
  const handleStartNewThread = async (user) => {
    try {
      setShowComposeModal(false);
      setSearchTerm('');
      setFilteredUsers(verifiedUsers);
      
      // Set the selected user
      setSelectedUser(user);
      setMessages([]);
      setIsCreatingThread(true);
      
      // Create a new thread object
      const newThread = {
        user,
        messages: [],
        lastMsg: null,
        unseenCount: 0,
        lastViewedTime: 0
      };
      
      // Add to threads list if not already present
      setThreads(prevThreads => {
        const existingThread = prevThreads.find(t => t.user.id === user.id);
        if (!existingThread) {
          return [newThread, ...prevThreads];
        }
        return prevThreads;
      });
      
      // Store current timestamp as last viewed time for this user
      const currentTime = new Date().toISOString();
      const lastViewedKey = `lastViewed_${user.id}`;
      localStorage.setItem(lastViewedKey, currentTime);
      
      console.log(`Started new thread with user: ${user.name} (${user.phoneNumber})`);
    } catch (error) {
      console.error('Error starting new thread:', error);
    }
  };

  // Handle unknown user thread selection
  const handleUnknownUserSelect = (thread) => {
    setUnknownUserThread(thread);
    setShowUnknownUserModal(true);
    fetchMatchingUsers();
  };

  // Fetch users for matching
  const fetchMatchingUsers = async () => {
    try {
      setLoadingMatchingUsers(true);
      
      // Get all users from Firebase
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const users = [];
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        users.push({
          id: doc.id,
          name: userData.name || userData.email || 'Unknown User',
          email: userData.email || '',
          phoneNumber: userData.phoneNumber || '',
          phoneVerified: userData.phoneVerified || false
        });
      });
      
      // Sort by name
      users.sort((a, b) => a.name.localeCompare(b.name));
      
      setMatchingUsers(users);
      setFilteredMatchingUsers(users);
      console.log(`Found ${users.length} users for matching`);
    } catch (error) {
      console.error('Error fetching matching users:', error);
      setMatchingUsers([]);
      setFilteredMatchingUsers([]);
    } finally {
      setLoadingMatchingUsers(false);
    }
  };

  // Filter matching users based on search term
  const filterMatchingUsers = (term) => {
    if (!term.trim()) {
      setFilteredMatchingUsers(matchingUsers);
      return;
    }
    
    const filtered = matchingUsers.filter(user => 
      user.name.toLowerCase().includes(term.toLowerCase()) ||
      user.email.toLowerCase().includes(term.toLowerCase()) ||
      user.phoneNumber.includes(term)
    );
    
    setFilteredMatchingUsers(filtered);
  };

  // Handle search input change for matching
  const handleMatchSearchChange = (e) => {
    const term = e.target.value;
    setMatchSearchTerm(term);
    filterMatchingUsers(term);
  };

  // Match unknown user to existing user
  const handleMatchUser = async (selectedUser) => {
    if (!unknownUserThread) return;
    
    try {
      console.log(`Matching unknown user ${unknownUserThread.user.phoneNumber} to user ${selectedUser.id}`);
      
      // First, get all unmatched messages for this phone number
      const unmatchedMessagesRef = collection(db, 'unmatched_messages');
      const normalizedUnknownPhone = normalizePhoneNumber(unknownUserThread.user.phoneNumber);
      const messagesQuery = query(
        unmatchedMessagesRef,
        where('phoneNumber', '==', normalizedUnknownPhone)
      );
      
      const snapshot = await getDocs(messagesQuery);
      
      if (snapshot.empty) {
        console.log('No unmatched messages found for this phone number');
        alert('No messages found to match.');
        return;
      }
      
      console.log(`Found ${snapshot.docs.length} messages to move`);
      
      // Process messages in smaller batches to avoid Firestore limits
      const batchSize = 500; // Firestore batch limit is 500 operations
      const messages = snapshot.docs;
      
      for (let i = 0; i < messages.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchMessages = messages.slice(i, i + batchSize);
        
        console.log(`Processing batch ${Math.floor(i / batchSize) + 1} with ${batchMessages.length} messages`);
        
        // Move messages to the imessages collection with the correct userId
        batchMessages.forEach(docSnapshot => {
          const messageData = docSnapshot.data();
          
          // Add to imessages collection in Realtime Database
          const messageRef = rtdbRef(rtdb, `imessages/${selectedUser.id}/${docSnapshot.id}`);
          // Note: We'll handle this separately since we can't mix Firestore and Realtime Database in the same batch
          
          // Delete from unmatched_messages
          batch.delete(docSnapshot.ref);
        });
        
        // Commit the Firestore batch first
        await batch.commit();
        
        // Now add messages to Realtime Database
        for (const docSnapshot of batchMessages) {
          const messageData = docSnapshot.data();
          const messageRef = rtdbRef(rtdb, `imessages/${selectedUser.id}/${docSnapshot.id}`);
          await set(messageRef, {
            ...messageData,
            userId: selectedUser.id,
            matchedAt: new Date().toISOString(),
            matchedBy: auth.currentUser?.email || auth.currentUser?.uid || 'unknown'
          });
        }
        console.log(`Committed batch ${Math.floor(i / batchSize) + 1}`);
      }
      
      // Update the user's phone number if it's not set or different
      if (!selectedUser.phoneNumber || normalizePhoneNumber(selectedUser.phoneNumber) !== normalizedUnknownPhone) {
        console.log(`Updating user ${selectedUser.id} phone number to ${normalizedUnknownPhone}`);
        await updateDoc(doc(db, 'users', selectedUser.id), {
          phoneNumber: normalizedUnknownPhone,
          phoneVerified: true,
          phoneNumberUpdatedAt: new Date()
        });
      }
      
      // Remove the unknown user thread from the list
      setThreads(prevThreads => 
        prevThreads.filter(t => t.user.id !== unknownUserThread.user.id)
      );
      
      // Close modal and refresh threads
      setShowUnknownUserModal(false);
      setUnknownUserThread(null);
      setMatchSearchTerm('');
      setFilteredMatchingUsers([]);
      
      // Refresh threads to show the matched messages in the correct thread
      setTimeout(() => {
        fetchThreads();
      }, 1000); // Small delay to ensure Firestore updates are propagated
      
      console.log(`Successfully matched unknown user to ${selectedUser.name}`);
      alert(`Successfully matched ${snapshot.docs.length} messages to ${selectedUser.name}`);
    } catch (error) {
      console.error('Error matching user:', error);
      alert(`Failed to match user: ${error.message}`);
    }
  };

  // Delete unknown user thread
  const handleDeleteUnknownUser = async () => {
    if (!unknownUserThread) return;
    
    if (!window.confirm(`Are you sure you want to delete all messages from ${unknownUserThread.user.phoneNumber}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      console.log(`Deleting unknown user thread for ${unknownUserThread.user.phoneNumber}`);
      
      // Delete all unmatched messages for this phone number
      const unmatchedMessagesRef = collection(db, 'unmatched_messages');
      const normalizedUnknownPhone = normalizePhoneNumber(unknownUserThread.user.phoneNumber);
      const deleteQuery = query(
        unmatchedMessagesRef,
        where('phoneNumber', '==', normalizedUnknownPhone)
      );
      
      const snapshot = await getDocs(deleteQuery);
      
      if (snapshot.empty) {
        console.log('No unmatched messages found for this phone number');
        alert('No messages found to delete.');
        return;
      }
      
      console.log(`Found ${snapshot.docs.length} messages to delete`);
      
      // Process deletions in smaller batches to avoid Firestore limits
      const batchSize = 500; // Firestore batch limit is 500 operations
      const messages = snapshot.docs;
      
      for (let i = 0; i < messages.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchMessages = messages.slice(i, i + batchSize);
        
        console.log(`Processing delete batch ${Math.floor(i / batchSize) + 1} with ${batchMessages.length} messages`);
        
        batchMessages.forEach(docSnapshot => {
          batch.delete(docSnapshot.ref);
        });
        
        // Commit this batch
        await batch.commit();
        console.log(`Committed delete batch ${Math.floor(i / batchSize) + 1}`);
      }
      
      // Remove the unknown user thread from the list
      setThreads(prevThreads => 
        prevThreads.filter(t => t.user.id !== unknownUserThread.user.id)
      );
      
      // Close modal
      setShowUnknownUserModal(false);
      setUnknownUserThread(null);
      setMatchSearchTerm('');
      setFilteredMatchingUsers([]);
      
      console.log(`Successfully deleted unknown user thread for ${unknownUserThread.user.phoneNumber}`);
      alert(`Successfully deleted ${snapshot.docs.length} messages from ${unknownUserThread.user.phoneNumber}`);
    } catch (error) {
      console.error('Error deleting unknown user:', error);
      alert(`Failed to delete unknown user: ${error.message}`);
    }
  };

  // Open compose modal
  const handleOpenCompose = () => {
    setShowComposeModal(true);
    fetchVerifiedUsers();
  };

  return (
    <Layout>
      <div className="imessage-threads-root">
        {/* Desktop split view: list left, chat right (inside main content, not sidebar) */}
        {!isMobile && (
          <>
            <div className="imessage-sidebar">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 16px 8px 16px'}}>
                <h3 style={{margin: 0}}>iMessage</h3>
                <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                  <button
                    onClick={handleOpenCompose}
                    title="New Message"
                    style={{
                      background: '#2563eb',
                      border: 'none',
                      color: '#fff',
                      cursor: 'pointer',
                      padding: '8px 12px',
                      borderRadius: 6,
                      fontSize: 14,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <FaPlus size={12} />
                    New
                  </button>
                  <button
                    onClick={() => {
                      fetchThreads();
                      if (selectedUser) {
                        refreshCurrentUserMessages();
                      }
                    }}
                    title="Refresh messages"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer',
                      padding: 4,
                      borderRadius: 4,
                      fontSize: 16
                    }}
                  >
                    ↻
                  </button>
                  {notificationSupported && (
                    <>
                      {notificationEnabled ? (
                        <button
                          onClick={handleTestNotification}
                          title="Test notification"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#2563eb',
                            cursor: 'pointer',
                            padding: 4,
                            borderRadius: 4,
                            fontSize: 16
                          }}
                        >
                          <FaBell />
                        </button>
                      ) : (
                        <button
                          onClick={() => setShowNotificationPrompt(true)}
                          title="Enable notifications"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#6b7280',
                            cursor: 'pointer',
                            padding: 4,
                            borderRadius: 4,
                            fontSize: 16
                          }}
                        >
                          <FaBellSlash />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {loading ? (
                <div className="imessage-empty" style={{padding: 24}}>Loading conversations...</div>
              ) : (
                <div className="imessage-thread-list">
                  {threads.map(thread => (
                    <div
                      key={thread.user.id}
                      className={`imessage-thread-item${selectedUser && selectedUser.id === thread.user.id ? ' selected' : ''}`}
                      onClick={() => thread.isUnknown ? handleUnknownUserSelect(thread) : handleSelectUser(thread)}
                      style={{ 
                        position: 'relative', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        borderLeft: thread.isUnknown ? '3px solid #f59e0b' : 'none',
                        background: thread.isUnknown ? '#fef3c7' : 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                        <div 
                          className="imessage-avatar" 
                          style={{
                            background: thread.isUnknown ? '#f59e0b' : '#e0e7ff',
                            color: thread.isUnknown ? '#fff' : '#2563eb'
                          }}
                        >
                          {thread.isUnknown ? '?' : (thread.user.name ? thread.user.name[0] : '?')}
                        </div>
                        <div className="imessage-thread-info" style={{ flex: 1, minWidth: 0 }}>
                          <div 
                            className="imessage-thread-name" 
                            style={{ 
                              color: thread.isUnknown ? '#92400e' : 'inherit',
                              fontWeight: thread.isUnknown ? '600' : 'normal'
                            }}
                          >
                            {thread.user.name || thread.user.email || 'N/A'}
                            {thread.isUnknown && (
                              <span style={{ 
                                fontSize: 11, 
                                color: '#f59e0b', 
                                marginLeft: 6,
                                background: '#fef3c7',
                                padding: '2px 6px',
                                borderRadius: 4,
                                fontWeight: '600'
                              }}>
                                UNKNOWN
                              </span>
                            )}
                          </div>
                          {/* Last message preview */}
                          {thread.lastMsg && (
                            <div className="imessage-thread-preview-row" style={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
                              <div className="imessage-thread-preview" style={{ color: '#6b7280', fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {thread.lastMsg.message ? thread.lastMsg.message.slice(0, 40) + (thread.lastMsg.message.length > 40 ? '…' : '') : (Array.isArray(thread.lastMsg.attachments) && thread.lastMsg.attachments.length > 0 ? '[Attachment]' : '')}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Timestamp and unread badge on the right */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 60 }}>
                        <div className="imessage-thread-timestamp" style={{ color: '#9ca3af', fontSize: 12, marginBottom: 2, minWidth: 48, textAlign: 'right' }}>
                          {thread.lastMsg && thread.lastMsg.timestamp && !isNaN(new Date(thread.lastMsg.timestamp).getTime())
                            ? (dayjs(thread.lastMsg.timestamp).isSame(dayjs(), 'day')
                                ? dayjs(thread.lastMsg.timestamp).format('h:mm A')
                                : dayjs(thread.lastMsg.timestamp).isSame(dayjs().subtract(1, 'day'), 'day')
                                  ? 'Yesterday'
                                  : dayjs(thread.lastMsg.timestamp).format('MMM D'))
                            : ''}
                        </div>
                        {thread.unseenCount > 0 && (
                          <span
                            style={{
                              background: thread.isUnknown ? '#f59e0b' : '#ef4444',
                              color: '#fff',
                              borderRadius: 8,
                              padding: '2px 8px',
                              fontSize: 11,
                              fontWeight: 700,
                              zIndex: 10,
                              display: 'inline-block',
                              marginTop: 2,
                              minWidth: 28,
                              textAlign: 'center',
                            }}
                            title={`Unread messages: ${thread.unseenCount}`}
                          >
                            {thread.unseenCount > 1 ? `${thread.unseenCount} UNREAD` : 'UNREAD'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="imessage-main">
              {selectedUser ? (
                loading ? (
                  <div className="imessage-empty" style={{ textAlign: 'center', padding: '40px 20px' }}>Loading messages...</div>
                ) : (
                  <div className="imessage-convo" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div className="imessage-convo-header">
                      <span className="imessage-convo-title">
                        {selectedUser.name || selectedUser.email}
                        {selectedUser.isUnknown && (
                          <span style={{ 
                            fontSize: 12, 
                            color: '#f59e0b', 
                            marginLeft: 8,
                            background: '#fef3c7',
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontWeight: '600'
                          }}>
                            UNKNOWN USER
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="imessage-convo-body" ref={convoBodyRef} onScroll={handleScroll} style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingBottom: 70 }}>
                      {loading ? (
                        <div className="imessage-empty">Loading messages...</div>
                      ) : messages.length === 0 ? (
                        <div className="imessage-empty">No messages yet.</div>
                      ) : (
                        dedupeMessages(messages).map((msg, idx) => {
                          const formattedTime = getFormattedTime(msg);
                          // Render image attachments if present
                          const hasImages = Array.isArray(msg.attachments) && msg.attachments.some(url => /\.(jpg|jpeg|png|gif|webp)$/i.test((typeof url === 'string' ? url.replace(/^"|"$/g, '') : url).split('?')[0]));
                          return (
                            <div key={msg.messageId || idx} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.direction === 'outbound' ? 'flex-end' : 'flex-start', width: '100%', marginBottom: msg.reaction ? 18 : 0 }}>
                              <div
                                className={`imessage-bubble ${msg.direction === 'outbound' ? 'sent' : 'received'}${newMsgId === msg.messageId ? ' new-msg-animate' : ''}`}
                                style={{
                                  maxWidth: '80%',
                                  minWidth: 48,
                                  margin: msg.direction === 'outbound' ? '4px 0 2px auto' : '4px auto 2px 0',
                                  background: msg.direction === 'outbound' ? '#2563eb' : '#f3f4f6',
                                  color: msg.direction === 'outbound' ? '#fff' : '#111827',
                                  borderRadius: 18,
                                  padding: '10px 14px',
                                  fontSize: 16,
                                  boxShadow: '0 2px 8px rgba(30,41,59,0.06)',
                                  position: 'relative',
                                  cursor: 'pointer',
                                  alignSelf: msg.direction === 'outbound' ? 'flex-end' : 'flex-start',
                                  wordBreak: 'break-word',
                                  minHeight: 36,
                                  display: 'inline-block',
                                  textAlign: 'left',
                                  width: 'auto',
                                  whiteSpace: 'pre-line',
                                  overflow: 'visible',
                                }}
                                onClick={(e) => {
                                  handleBubbleClick(msg, e);
                                  if (msg.direction === 'outbound' || msg.direction === 'inbound') setShowSentByUser(msg.messageId === showSentByUser ? null : msg.messageId);
                                  else setShowSentByUser(null);
                                }}
                              >
                                {/* Reaction badge — iMessage-style corner badge outside the bubble */}
                                {msg.reaction && (
                                  <span className="message-reaction" style={{
                                    position: 'absolute',
                                    bottom: -18,
                                    right: msg.direction === 'outbound' ? 8 : 'auto',
                                    left: msg.direction === 'outbound' ? 'auto' : 8,
                                    fontSize: 16,
                                    lineHeight: 1,
                                    background: '#fff',
                                    border: '1.5px solid #e5e7eb',
                                    borderRadius: 12,
                                    padding: '2px 6px',
                                    boxShadow: '0 1px 4px rgba(30,41,59,0.12)',
                                    zIndex: 5,
                                    userSelect: 'none',
                                  }}>
                                    {REACTIONS.find(r => r.key === msg.reaction)?.label || msg.reaction}
                                  </span>
                                )}
                                {/* Unread indicator for inbound, unseen messages */}
                                {msg.direction === 'inbound' && msg.seen === false && (
                                  <span
                                    style={{
                                      position: 'absolute',
                                      top: 6,
                                      right: 10,
                                      width: 12,
                                      height: 12,
                                      background: '#ef4444',
                                      borderRadius: '50%',
                                      border: '2px solid #fff',
                                      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                      zIndex: 10,
                                      display: 'inline-block'
                                    }}
                                    title="Unread"
                                  />
                                )}
                                <div className="imessage-bubble-content">
                                  {msg.message && <p style={{margin: 0, width: '100%', wordBreak: 'break-word'}}>{msg.message}</p>}
                                  {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                                    <div className="message-attachments" style={{width: '100%'}}>
                                      {msg.attachments.map((url, i) => {
                                        const cleanUrl = typeof url === 'string' ? url.replace(/^"|"$/g, '') : url;
                                        const baseUrl = cleanUrl.split('?')[0];
                                        if (/\.(jpg|jpeg|png|gif|webp)$/i.test(baseUrl)) {
                                          return (
                                            <img
                                              key={i}
                                              src={cleanUrl}
                                              alt="attachment"
                                              className="message-image"
                                              style={{ maxWidth: 240, maxHeight: 240, margin: '8px 0', borderRadius: 8, display: 'block', boxShadow: '0 2px 8px rgba(30,41,59,0.10)', cursor: 'pointer', marginLeft: 'auto', marginRight: 'auto' }}
                                              onClick={e => { e.stopPropagation(); setImageModal({ open: true, url: cleanUrl }); }}
                                              onError={e => { e.target.style.display = 'none'; }}
                                            />
                                          );
                                        }
                                        if (/\.(mp3)$/i.test(baseUrl)) {
                                          return (
                                            <audio key={i} controls style={{ margin: '8px 0', width: '100%' }}>
                                              <source src={cleanUrl} type="audio/mpeg" />
                                              Your browser does not support the audio element.
                                            </audio>
                                          );
                                        }
                                        return (
                                          <a key={i} href={cleanUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', color: '#2563eb', textDecoration: 'underline', display: 'block', margin: '8px 0' }}>
                                            {cleanUrl}
                                          </a>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Recall button for outbound messages */}
                                {msg.direction === 'outbound' && !msg.recalled && (
                                  <div style={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    zIndex: 15
                                  }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRecallMessage(msg);
                                      }}
                                      disabled={recallingMessage === msg.messageId}
                                      style={{
                                        background: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: 24,
                                        height: 24,
                                        fontSize: 12,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        opacity: recallingMessage === msg.messageId ? 0.6 : 1
                                      }}
                                      title="Recall message"
                                    >
                                      {recallingMessage === msg.messageId ? '⏳' : '×'}
                                    </button>
                                  </div>
                                )}
                                
                                {/* Recalled indicator */}
                                {msg.recalled && (
                                  <div style={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    background: '#6b7280',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 24,
                                    height: 24,
                                    fontSize: 12,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                  }}
                                  title={`Recalled at ${msg.recalledAt ? new Date(msg.recalledAt).toLocaleTimeString() : 'unknown time'}`}
                                  >
                                    ✓
                                  </div>
                                )}
                                
                                {/* Sender label underneath the bubble, centered */}
                                {showSentByUser === msg.messageId && (
                                  <div style={{ background: '#fff', color: '#2563eb', fontWeight: 600, fontSize: 13, borderRadius: 8, padding: '3px 14px', boxShadow: '0 2px 8px rgba(30,41,59,0.10)', zIndex: 12, marginTop: 8, whiteSpace: 'nowrap', maxWidth: 240, textAlign: 'center', alignSelf: msg.direction === 'outbound' ? 'flex-end' : 'flex-start' }}>
                                    Sent by {getSenderName(msg)}
                                  </div>
                                )}
                              </div>
                              {/* Timestamp below the bubble, left or right aligned to match bubble */}
                              {formattedTime && (
                                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2, marginBottom: 8, textAlign: msg.direction === 'outbound' ? 'right' : 'left', width: '80%' }}>{formattedTime}</div>
                              )}
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                    
                    {/* Recall error display */}
                    {recallError && (
                      <div style={{
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#dc2626',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        margin: '8px 0',
                        fontSize: '14px',
                        textAlign: 'center'
                      }}>
                        {recallError}
                      </div>
                    )}
                    
                    <div className="imessage-convo-input" style={{ display: 'flex', alignItems: 'center' }}>
                      {/* Image upload button */}
                      <label htmlFor="image-upload-input" style={{ marginRight: 8, cursor: uploadingImage ? 'not-allowed' : 'pointer', opacity: uploadingImage ? 0.5 : 1 }}>
                        <span role="img" aria-label="Upload Image" style={{ fontSize: 22 }}>📎</span>
                        <input
                          id="image-upload-input"
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          disabled={uploadingImage}
                          onChange={handleImageUpload}
                        />
                      </label>
                      <textarea
                        value={newMessage}
                        onChange={e => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        rows={2}
                        disabled={sendingMessage}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                        style={{ flex: 1 }}
                      />
                      <button onClick={() => handleSendMessage()} disabled={!newMessage.trim() || sendingMessage} className="imessage-send-btn">
                        <FaComment />
                      </button>
                    </div>
                    {/* Image modal */}
                    {imageModal.open && (
                      <div className="image-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(20,24,31,0.92)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeInModal 0.25s' }} onClick={() => setImageModal({ open: false, url: '' })}>
                        <div className="image-modal-content" style={{ position: 'relative', textAlign: 'center', animation: 'scaleInModal 0.3s' }} onClick={e => e.stopPropagation()}>
                          <img src={imageModal.url} alt="Full" style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 16, boxShadow: '0 4px 24px rgba(30,41,59,0.18)', background: '#fff' }} />
                          <a
                            href={imageModal.url}
                            download
                            className="download-btn"
                            style={{ display: 'inline-block', marginTop: 18, background: '#2563eb', color: '#fff', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 16 }}
                          >
                            Download
                          </a>
                          <button
                            onClick={() => setImageModal({ open: false, url: '' })}
                            style={{ position: 'absolute', top: 16, right: 24, background: 'none', border: 'none', fontSize: 32, color: '#fff', cursor: 'pointer', textShadow: '0 2px 8px rgba(30,41,59,0.25)' }}
                            aria-label="Close"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div className="imessage-empty-main">Select a conversation to view messages</div>
              )}
            </div>
          </>
        )}
      {/* Mobile: list/chat toggle */}
      {isMobile && mobileView === 'list' && (
        <div className="imessage-list-mobile-wrapper">
          <div className="imessage-header-mobile" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#fff', fontWeight: 700, fontSize: 18}}>
            <span>iMessage</span>
            <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
              <button
                onClick={handleOpenCompose}
                title="New Message"
                style={{
                  background: '#2563eb',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  padding: '6px 10px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <FaPlus size={10} />
                New
              </button>
              <button
                onClick={() => {
                  clearThreadsCache(); // Clear cache for fresh data
                  fetchThreads();
                  if (selectedUser) {
                    refreshCurrentUserMessages();
                  }
                }}
                title="Refresh messages"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: 4,
                  borderRadius: 4,
                  fontSize: 16
                }}
              >
                ↻
              </button>
              {/* Single notification button */}
              <button
                onClick={notificationEnabled ? handleTestNotification : () => setShowNotificationPrompt(true)}
                title={notificationEnabled ? "Test notification" : "Enable notifications"}
                style={{
                  background: 'none',
                  border: 'none',
                  color: notificationEnabled ? '#2563eb' : '#6b7280',
                  cursor: 'pointer',
                  padding: 4,
                  borderRadius: 4,
                  fontSize: 16
                }}
              >
                {notificationEnabled ? <FaBell /> : <FaBellSlash />}
              </button>
            </div>
          </div>
          {loading ? (
            <div className="imessage-empty" style={{padding: 24}}>Loading conversations...</div>
          ) : (
            <div className="imessage-thread-list-mobile">
              {threads.map(thread => (
                <div
                  key={thread.user.id}
                  className={`imessage-thread-item${selectedUser && selectedUser.id === thread.user.id && mobileView === 'chat' ? ' selected' : ''}`}
                  onClick={() => thread.isUnknown ? handleUnknownUserSelect(thread) : handleSelectUserMobile(thread)}
                  style={{
                    borderLeft: thread.isUnknown ? '3px solid #f59e0b' : 'none',
                    background: thread.isUnknown ? '#fef3c7' : 'transparent',
                    cursor: 'pointer'
                  }}
                >
                  <div 
                    className="imessage-avatar" 
                    style={{
                      width: 38, 
                      height: 38, 
                      borderRadius: '50%', 
                      background: thread.isUnknown ? '#f59e0b' : '#e0e7ff', 
                      color: thread.isUnknown ? '#fff' : '#2563eb', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontWeight: 700, 
                      fontSize: 18, 
                      marginRight: 12
                    }}
                  >
                    {thread.isUnknown ? '?' : (thread.user.name ? thread.user.name[0] : '?')}
                  </div>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={{
                      fontWeight: 600, 
                      fontSize: 15, 
                      color: thread.isUnknown ? '#92400e' : '#111827', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      {thread.user.name || thread.user.email || 'N/A'}
                      {thread.isUnknown && (
                        <span style={{ 
                          fontSize: 10, 
                          color: '#f59e0b', 
                          background: '#fef3c7',
                          padding: '1px 4px',
                          borderRadius: 3,
                          fontWeight: '600'
                        }}>
                          UNKNOWN
                        </span>
                      )}
                    </div>
                    <div style={{fontSize: 13, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{thread.lastMsg && thread.lastMsg.message ? thread.lastMsg.message.slice(0, 40) + (thread.lastMsg.message.length > 40 ? '…' : '') : (Array.isArray(thread.lastMsg.attachments) && thread.lastMsg.attachments.length > 0 ? '[Attachment]' : '')}</div>
                  </div>
                  <div style={{marginLeft: 8, fontSize: 12, color: '#9ca3af', flexShrink: 0}}>
                    {thread.lastMsg && thread.lastMsg.timestamp && !isNaN(new Date(thread.lastMsg.timestamp).getTime())
                      ? (dayjs(thread.lastMsg.timestamp).isSame(dayjs(), 'day')
                          ? dayjs(thread.lastMsg.timestamp).format('h:mm A')
                          : dayjs(thread.lastMsg.timestamp).isSame(dayjs().subtract(1, 'day'), 'day') ? 'Yesterday' : dayjs(thread.lastMsg.timestamp).format('MMM D'))
                      : ''}
                  </div>
                  {thread.unseenCount > 0 && (
                    <span className="imessage-unseen-badge">
                      <FaCircle color={thread.isUnknown ? "#f59e0b" : "#ef4444"} size={12}/>
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {isMobile && mobileView === 'chat' && (
        <div className="imessage-main" style={{ width: '100vw', minWidth: 0, maxWidth: '100vw', height: '100vh', position: 'relative' }}>
          <button onClick={handleBackToList} style={{ position: 'absolute', top: 10, left: 10, zIndex: 30, background: 'none', border: 'none', fontSize: 22, color: '#2563eb', fontWeight: 700, padding: 4, borderRadius: 8 }} aria-label="Back to conversation list">←</button>
          {selectedUser ? (
            loading ? (
              <div className="imessage-empty" style={{ textAlign: 'center', padding: '40px 20px' }}>Loading messages...</div>
            ) : (
              <div className="imessage-convo" style={{ marginTop: 0 }}>
                <div className="imessage-convo-header" style={{ paddingLeft: 44 }}>
                  <span className="imessage-convo-title">
                    {selectedUser.name || selectedUser.email}
                    {selectedUser.isUnknown && (
                      <span style={{ 
                        fontSize: 11, 
                        color: '#f59e0b', 
                        marginLeft: 6,
                        background: '#fef3c7',
                        padding: '1px 4px',
                        borderRadius: 3,
                        fontWeight: '600'
                      }}>
                        UNKNOWN
                      </span>
                    )}
                  </span>
                </div>
                <div className="imessage-convo-body" ref={convoBodyRef} onScroll={handleScroll} style={{paddingBottom: 120}}>
                  {loading ? (
                    <div className="imessage-empty">Loading messages...</div>
                  ) : messages.length === 0 ? (
                    <div className="imessage-empty">No messages yet.</div>
                  ) : (
                    dedupeMessages(messages).map((msg, idx) => (
                      <div
                        key={msg.messageId || idx}
                        style={{ marginBottom: msg.reaction ? 18 : 0 }}
                      >
                      <div
                        className={`imessage-bubble ${msg.direction === 'outbound' ? 'sent' : 'received'}${newMsgId === msg.messageId ? ' new-msg-animate' : ''}`}
                        style={{ position: 'relative', cursor: 'pointer', overflow: 'visible' }}
                        onClick={(e) => {
                          handleBubbleClick(msg, e);
                          if (msg.direction === 'outbound' || msg.direction === 'inbound') setShowSentByUser(msg.messageId === showSentByUser ? null : msg.messageId);
                          else setShowSentByUser(null);
                        }}
                      >
                        {/* Reaction badge — iMessage-style corner badge */}
                        {msg.reaction && (
                          <span className="message-reaction" style={{
                            position: 'absolute',
                            bottom: -18,
                            right: msg.direction === 'outbound' ? 8 : 'auto',
                            left: msg.direction === 'outbound' ? 'auto' : 8,
                            fontSize: 16,
                            lineHeight: 1,
                            background: '#fff',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: 12,
                            padding: '2px 6px',
                            boxShadow: '0 1px 4px rgba(30,41,59,0.12)',
                            zIndex: 5,
                            userSelect: 'none',
                          }}>
                            {REACTIONS.find(r => r.key === msg.reaction)?.label || msg.reaction}
                          </span>
                        )}
                        {/* Unread indicator for inbound, unseen messages */}
                        {msg.direction === 'inbound' && msg.seen === false && (
                          <span
                            style={{
                              position: 'absolute',
                              top: 6,
                              right: 10,
                              width: 12,
                              height: 12,
                              background: '#ef4444',
                              borderRadius: '50%',
                              border: '2px solid #fff',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                              zIndex: 10,
                              display: 'inline-block'
                            }}
                            title="Unread"
                          />
                        )}
                        <div className="imessage-bubble-content">
                          {msg.message && <p>{msg.message}</p>}
                          {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                            <div className="message-attachments">
                              {msg.attachments.map((url, i) => {
                                const cleanUrl = typeof url === 'string' ? url.replace(/^"|"$/g, '') : url;
                                const baseUrl = cleanUrl.split('?')[0];
                                if (/\.(jpg|jpeg|png|gif|webp)$/i.test(baseUrl)) {
                                  return (
                                    <img
                                      key={i}
                                      src={cleanUrl}
                                      alt="attachment"
                                      className="message-image"
                                      style={{ maxWidth: 240, maxHeight: 240, margin: '8px 0', borderRadius: 8, display: 'block', boxShadow: '0 2px 8px rgba(30,41,59,0.10)', cursor: 'pointer' }}
                                      onClick={e => { e.stopPropagation(); setImageModal({ open: true, url: cleanUrl }); }}
                                      onError={e => { e.target.style.display = 'none'; }}
                                    />
                                  );
                                }
                                if (/\.(mp3)$/i.test(baseUrl)) {
                                  return (
                                    <audio key={i} controls style={{ margin: '8px 0', width: '100%' }}>
                                      <source src={cleanUrl} type="audio/mpeg" />
                                      Your browser does not support the audio element.
                                    </audio>
                                  );
                                }
                                return (
                                  <a key={i} href={cleanUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', color: '#2563eb', textDecoration: 'underline', display: 'block', margin: '8px 0' }}>
                                    {cleanUrl}
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="imessage-bubble-time" style={msg.direction === 'outbound' ? { color: '#e0e7ff' } : {}}>
                          {msg.timestamp ? new Date(msg.timestamp).toLocaleString() : ''}
                        </div>
                        {/* Sender label below the bubble, not absolutely positioned */}
                        {showSentByUser === msg.messageId && (
                          <div style={{ background: '#fff', color: '#2563eb', fontWeight: 600, fontSize: 13, borderRadius: 8, padding: '3px 14px', boxShadow: '0 2px 8px rgba(30,41,59,0.10)', zIndex: 12, marginTop: 8, whiteSpace: 'nowrap', maxWidth: 240, textAlign: 'center', alignSelf: msg.direction === 'outbound' ? 'flex-end' : 'flex-start' }}>
                            Sent by {getSenderName(msg)}
                          </div>
                        )}
                      </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
                <div className="imessage-convo-input" style={{ display: 'flex', alignItems: 'center' }}>
                  {/* Image upload button */}
                  <label htmlFor="image-upload-input" style={{ marginRight: 8, cursor: uploadingImage ? 'not-allowed' : 'pointer', opacity: uploadingImage ? 0.5 : 1 }}>
                    <span role="img" aria-label="Upload Image" style={{ fontSize: 22 }}>📎</span>
                    <input
                      id="image-upload-input"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      disabled={uploadingImage}
                      onChange={handleImageUpload}
                    />
                  </label>
                  <textarea
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows={2}
                    disabled={sendingMessage}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                    style={{ flex: 1 }}
                  />
                  <button onClick={() => handleSendMessage()} disabled={!newMessage.trim() || sendingMessage} className="imessage-send-btn">
                    <FaComment />
                  </button>
                </div>
                {/* Image modal */}
                {imageModal.open && (
                  <div className="image-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(20,24,31,0.92)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeInModal 0.25s' }} onClick={() => setImageModal({ open: false, url: '' })}>
                    <div className="image-modal-content" style={{ position: 'relative', textAlign: 'center', animation: 'scaleInModal 0.3s' }} onClick={e => e.stopPropagation()}>
                      <img src={imageModal.url} alt="Full" style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 16, boxShadow: '0 4px 24px rgba(30,41,59,0.18)', background: '#fff' }} />
                      <a
                        href={imageModal.url}
                        download
                        className="download-btn"
                        style={{ display: 'inline-block', marginTop: 18, background: '#2563eb', color: '#fff', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 16 }}
                      >
                        Download
                      </a>
                      <button
                        onClick={() => setImageModal({ open: false, url: '' })}
                        style={{ position: 'absolute', top: 16, right: 24, background: 'none', border: 'none', fontSize: 32, color: '#fff', cursor: 'pointer', textShadow: '0 2px 8px rgba(30,41,59,0.25)' }}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          ) : null}
        </div>
      )}

      {/* Compose Modal */}
      {showComposeModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowComposeModal(false)}
        >
          <div 
            style={{
              background: 'white',
              padding: '0',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{margin: 0, color: '#1f2937', fontSize: '18px', fontWeight: '600'}}>New Message</h3>
              <button
                onClick={() => setShowComposeModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Search Input */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <FaSearch style={{
                  position: 'absolute',
                  left: '12px',
                  color: '#9ca3af',
                  fontSize: '16px'
                }} />
                <input
                  type="text"
                  placeholder="Search users by name, email, or phone..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
            </div>

            {/* Users List */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              maxHeight: '400px'
            }}>
              {loadingVerifiedUsers ? (
                <div style={{
                  padding: '40px 24px',
                  textAlign: 'center',
                  color: '#6b7280'
                }}>
                  Loading verified users...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div style={{
                  padding: '40px 24px',
                  textAlign: 'center',
                  color: '#6b7280'
                }}>
                  {searchTerm ? 'No users found matching your search.' : 'No verified users found.'}
                </div>
              ) : (
                <div>
                  {filteredUsers.map(user => (
                    <div
                      key={user.id}
                      onClick={() => handleStartNewThread(user)}
                      style={{
                        padding: '16px 24px',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: '#e0e7ff',
                        color: '#2563eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '16px',
                        flexShrink: 0
                      }}>
                        {user.name ? user.name[0].toUpperCase() : '?'}
                      </div>
                      
                      {/* User Info */}
                      <div style={{flex: 1, minWidth: 0}}>
                        <div style={{
                          fontWeight: '600',
                          fontSize: '15px',
                          color: '#111827',
                          marginBottom: '2px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {user.name}
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: '#6b7280',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {user.email}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#9ca3af',
                          marginTop: '2px'
                        }}>
                          {user.phoneNumber}
                        </div>
                      </div>
                      
                      {/* Verified Badge */}
                      <div style={{
                        background: '#dcfce7',
                        color: '#166534',
                        fontSize: '11px',
                        fontWeight: '600',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        flexShrink: 0
                      }}>
                        Verified
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unknown User Management Modal */}
      {showUnknownUserModal && unknownUserThread && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowUnknownUserModal(false)}
        >
          <div 
            style={{
              background: 'white',
              padding: '0',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#fef3c7'
            }}>
              <div>
                <h3 style={{margin: 0, color: '#92400e', fontSize: '18px', fontWeight: '600'}}>
                  Unknown User Management
                </h3>
                <p style={{margin: '4px 0 0 0', color: '#92400e', fontSize: '14px'}}>
                  Phone: {unknownUserThread.user.phoneNumber}
                </p>
              </div>
              <button
                onClick={() => setShowUnknownUserModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#92400e',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px'
                }}
              >
                ×
              </button>
            </div>

            {/* Messages Preview (taller, scrollable) */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #e5e7eb',
              maxHeight: '350px',
              minHeight: '120px',
              overflowY: 'auto',
              background: '#f9fafb',
              marginBottom: 0
            }}>
              <h4 style={{margin: '0 0 12px 0', color: '#1f2937', fontSize: '16px'}}>
                Messages ({unknownUserThread.messages.length})
              </h4>
              {unknownUserThread.messages.length === 0 && (
                <div style={{color: '#6b7280', fontSize: 14}}>No messages.</div>
              )}
              {unknownUserThread.messages.map((msg, index) => (
                <div key={index} style={{
                  padding: '8px 12px',
                  background: '#fff',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  fontSize: '14px',
                  color: '#374151',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                }}>
                  <div style={{fontWeight: '600', marginBottom: '4px', fontSize: 12, color: '#6b7280'}}>
                    {msg.timestamp && !isNaN(new Date(msg.timestamp)) ? new Date(msg.timestamp).toLocaleString() : 'Unknown Date'}
                  </div>
                  <div>{msg.message || '[No text]'}</div>
                </div>
              ))}
            </div>

            {/* Reply to Unknown User */}
            <div style={{padding: '12px 24px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb'}}>
              <form style={{display: 'flex', flexDirection: 'column', gap: 8}} onSubmit={async (e) => {
                e.preventDefault();
                if (!replyUnknownMsg.trim()) return;
                setSendingUnknownReply(true);
                try {
                  const normalizedUnknownPhone = normalizePhoneNumber(unknownUserThread.user.phoneNumber);
                  const res = await fetch(`${API_BASE}/api/send-imessage`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      phoneNumber: normalizedUnknownPhone,
                      message: replyUnknownMsg,
                      userId: null // No userId for unknown
                    })
                  });
                  if (res.ok) {
                    setReplyUnknownMsg('');
                    fetchThreads();
                  } else {
                    alert('Failed to send message.');
                  }
                } catch (err) {
                  alert('Failed to send message.');
                }
                setSendingUnknownReply(false);
              }}>
                <textarea
                  value={replyUnknownMsg}
                  onChange={e => setReplyUnknownMsg(e.target.value)}
                  placeholder="Reply to this number..."
                  rows={2}
                  style={{flex: 1, resize: 'vertical', minHeight: 60, maxHeight: 150, fontSize: 15, borderRadius: 6, border: '1px solid #d1d5db', padding: 10}}
                  disabled={sendingUnknownReply}
                />
                <button type="submit" disabled={!replyUnknownMsg.trim() || sendingUnknownReply} style={{background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, fontSize: 15, cursor: 'pointer'}}>
                  Send
                </button>
              </form>
            </div>

            {/* Action Buttons */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #e5e7eb',
              background: '#fff'
            }}>
              <div style={{display: 'flex', gap: '12px', marginBottom: '16px'}}>
                <button
                  onClick={handleDeleteUnknownUser}
                  style={{
                    padding: '10px 16px',
                    border: '1px solid #ef4444',
                    background: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: '#ef4444',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  🗑️ Delete All Messages
                </button>
                <button
                  onClick={() => setShowUnknownUserModal(false)}
                  style={{
                    padding: '10px 16px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: '#6b7280',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
              
              {/* Expandable Match Section */}
              <div style={{
                padding: '12px',
                background: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd',
                marginTop: 8
              }}>
                <button
                  onClick={() => setShowMatchSectionOpen(v => !v)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0369a1',
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: 'pointer',
                    marginBottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  {matchSectionOpen ? '▼' : '►'} Match to Existing User
                </button>
                {matchSectionOpen && (
                  <>
                    <p style={{margin: '0 0 12px 0', color: '#0369a1', fontSize: '13px'}}>
                      Select a user below to match these messages to their account. This will move all messages and update their phone number.
                    </p>
                    {/* Search Input */}
                    <div style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <FaSearch style={{
                        position: 'absolute',
                        left: '12px',
                        color: '#9ca3af',
                        fontSize: '14px'
                      }} />
                      <input
                        type="text"
                        placeholder="Search users by name, email, or phone..."
                        value={matchSearchTerm}
                        onChange={handleMatchSearchChange}
                        style={{
                          width: '100%',
                          padding: '8px 8px 8px 32px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '13px',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Users List (expandable) */}
            {matchSectionOpen && (
              <div style={{
                flex: 1,
                overflowY: 'auto',
                maxHeight: '300px',
                background: '#f0f9ff',
                borderTop: '1px solid #bae6fd'
              }}>
                {loadingMatchingUsers ? (
                  <div style={{
                    padding: '40px 24px',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    Loading users...
                  </div>
                ) : filteredMatchingUsers.length === 0 ? (
                  <div style={{
                    padding: '40px 24px',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    {matchSearchTerm ? 'No users found matching your search.' : 'No users found.'}
                  </div>
                ) : (
                  <div>
                    {filteredMatchingUsers.map(user => (
                      <div
                        key={user.id}
                        onClick={() => handleMatchUser(user)}
                        style={{
                          padding: '12px 24px',
                          borderBottom: '1px solid #f3f4f6',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        {/* Avatar */}
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: '#e0e7ff',
                          color: '#2563eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700',
                          fontSize: '14px',
                          flexShrink: 0
                        }}>
                          {user.name ? user.name[0].toUpperCase() : '?'}
                        </div>
                        
                        {/* User Info */}
                        <div style={{flex: 1, minWidth: 0}}>
                          <div style={{
                            fontWeight: '600',
                            fontSize: '14px',
                            color: '#111827',
                            marginBottom: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {user.name}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {user.email}
                          </div>
                          {user.phoneNumber && (
                            <div style={{
                              fontSize: '11px',
                              color: '#9ca3af',
                              marginTop: '2px'
                            }}>
                              {user.phoneNumber}
                            </div>
                          )}
                        </div>
                        
                        {/* Match Button */}
                        <button
                          style={{
                            background: '#2563eb',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        >
                          Match
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notification Prompt Modal */}
      {showNotificationPrompt && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowNotificationPrompt(false)}
        >
          <div 
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{fontSize: '48px', marginBottom: '16px'}}>🔔</div>
            <h3 style={{margin: '0 0 12px 0', color: '#1f2937'}}>Enable Notifications</h3>
            <p style={{margin: '0 0 24px 0', color: '#6b7280', lineHeight: '1.5'}}>
              Get notified when you receive new messages, even when the app is not open.
            </p>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'center'}}>
              <button
                onClick={() => setShowNotificationPrompt(false)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                Not Now
              </button>
              <button
                onClick={handleEnableNotifications}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: '#2563eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'white',
                  fontWeight: '600'
                }}
              >
                Enable
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Fixed-position reaction picker — renders above the overflow container so it's never clipped */}
      {activeReactionMsg && reactionPickerPos && (
        <div
          className="reaction-menu"
          style={{
            position: 'fixed',
            top: reactionPickerPos.y - 8,
            left: reactionPickerPos.x,
            transform: 'translate(-50%, -100%)',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 20,
            zIndex: 9999,
            padding: '8px 12px',
            display: 'flex',
            gap: 8,
            boxShadow: '0 4px 16px rgba(30,41,59,0.18)',
            minWidth: 220,
            maxWidth: 'min(320px, 90vw)',
            justifyContent: 'center',
            pointerEvents: 'auto',
          }}
          onClick={e => e.stopPropagation()}
        >
          {REACTIONS.map(r => (
            <button
              key={r.key}
              title={r.key}
              style={{ fontSize: 26, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 2px', borderRadius: 8, transition: 'transform 0.12s', outline: 'none', lineHeight: 1 }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.3)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              onClick={e => { e.stopPropagation(); handleSelectReaction(r.key, reactionPickerPos.msg); }}
            >{r.label}</button>
          ))}
        </div>
      )}
    </div>
    </Layout>
  );
};

export default IMessageThreads; 